import { firestore } from "../../firebase/firebase";
import { updatePendingStatusAfterChange } from "../../utils/pendingItemsCalculator";
import firebase from "firebase";

/**
 * Listener para detectar cambios en respuestas (RespuestasOT) y recalcular items pendientes
 * Este listener se activa cuando las aplicaciones móviles o Cloud Functions actualizan respuestas
 */
export class ResponseChangeListener {
  private static listeners: { [key: string]: () => void } = {};

  /**
   * Inicia el listener para un checklist específico
   */
  static async startListening(
    empresaId: string,
    projectId: string,
    checklistId: string
  ) {
    const listenerId = `${empresaId}-${projectId}-${checklistId}`;
    
    // Si ya existe un listener para este checklist, lo cerramos primero
    if (this.listeners[listenerId]) {
      this.listeners[listenerId]();
    }

    console.log(`[ResponseListener] Iniciando listener para checklist: ${checklistId}`);

    // Crear query alternativo: escuchar cambios en espacios asignados específicos
    // En lugar de usar collectionGroup (que requiere índice), escucharemos cada espacio individualmente
    
    console.log(`[ResponseListener] Obteniendo espacios asignados para el checklist: ${checklistId}`);
    
    // Primero obtenemos todos los espacios asignados del checklist
    const checklistRef = firestore
      .collection('Empresas').doc(empresaId)
      .collection('ProyectosOT').doc(projectId)
      .collection('ChecklistsOT').doc(checklistId);
    
    const espaciosSnapshot = await checklistRef.collection('EspaciosAsignadosOT').get();
    
    if (espaciosSnapshot.empty) {
      console.log(`[ResponseListener] No hay espacios asignados para el checklist: ${checklistId}`);
      return () => {}; // Retornar función vacía
    }

    // Crear listeners individuales para cada espacio
    const unsubscribeFunctions: Array<() => void> = [];

    espaciosSnapshot.docs.forEach(espacioDoc => {
      const espacioId = espacioDoc.id;
      const responsesRef = espacioDoc.ref.collection('RespuestasOT');
      
      console.log(`[ResponseListener] Configurando listener para espacio: ${espacioId}`);
      
      const unsubscribe = responsesRef.onSnapshot({
        next: async (snapshot) => {
          const modifiedDocs = snapshot.docChanges().filter(change => change.type === 'modified');
          
          if (modifiedDocs.length === 0) {
            return;
          }

          console.log(`[ResponseListener] 🔄 Detectados ${modifiedDocs.length} cambios en respuestas para espacio: ${espacioId}`);
          
          // Procesar cada cambio individualmente para extraer los IDs correctos
          for (const change of modifiedDocs) {
            const docData = change.doc.data();
            console.log(`[ResponseListener] 📄 Procesando cambio:`, {
              docId: change.doc.id,
              estado: docData?.Estado,
              hasRevisores: !!docData?.Revisores,
              revisorCount: docData?.Revisores?.length || 0,
              path: change.doc.ref.path
            });

            try {
              // Extraer IDs correctos de la ruta del documento que cambió
              // Ruta: empresas/{empresaId}/ProyectosOT/{projectId}/ChecklistsOT/{checklistId}/EspaciosAsignadosOT/{espacioId}/RespuestasOT/{responseId}
              const pathParts = change.doc.ref.path.split('/');
              const realEmpresaId = pathParts[1];
              const realProjectId = pathParts[3];
              const realChecklistId = pathParts[5];  // Este es el ID REAL del checklist
              const realEspacioId = pathParts[7];

              console.log(`[ResponseListener] 🔍 IDs extraídos de la ruta:`, {
                empresa: realEmpresaId,
                proyecto: realProjectId, 
                checklist: realChecklistId,
                espacio: realEspacioId,
                parametrosOriginales: { empresaId, projectId, checklistId, espacioId }
              });

              console.log(`[ResponseListener] 🚀 Iniciando recálculo con IDs reales`);
              await updatePendingStatusAfterChange(
                realEmpresaId,
                realProjectId,
                realChecklistId,
                realEspacioId
              );
              
              console.log(`[ResponseListener] ✅ Recálculo completado para checklist real: ${realChecklistId}`);
            } catch (error) {
              console.warn(`[ResponseListener] ❌ Error recalculando cambio ${change.doc.id}:`, error);
            }
          }
        },
        error: (error) => {
          console.error(`[ResponseListener] Error en listener para espacio ${espacioId}:`, error);
        }
      });

      unsubscribeFunctions.push(unsubscribe);
    });

    // Crear función de unsubscribe que cierre todos los listeners individuales
    const masterUnsubscribe = () => {
      console.log(`[ResponseListener] Cerrando ${unsubscribeFunctions.length} listeners para checklist: ${checklistId}`);
      unsubscribeFunctions.forEach(unsub => unsub());
    };

    // Guardar la función de unsubscribe
    this.listeners[listenerId] = masterUnsubscribe;
    
    return masterUnsubscribe;
  }

  /**
   * Detiene el listener para un checklist específico
   */
  static stopListening(empresaId: string, projectId: string, checklistId: string) {
    const listenerId = `${empresaId}-${projectId}-${checklistId}`;
    
    if (this.listeners[listenerId]) {
      console.log(`[ResponseListener] Deteniendo listener para checklist: ${checklistId}`);
      this.listeners[listenerId]();
      delete this.listeners[listenerId];
    }
  }

  /**
   * Detiene todos los listeners activos
   */
  static stopAllListeners() {
    console.log(`[ResponseListener] Deteniendo ${Object.keys(this.listeners).length} listeners activos`);
    
    Object.values(this.listeners).forEach(unsubscribe => unsubscribe());
    this.listeners = {};
  }

  /**
   * Obtiene la lista de listeners activos
   */
  static getActiveListeners(): string[] {
    return Object.keys(this.listeners);
  }
}